// src/pages/DebtStudio/components/Issues/DealStructure/hooks/levelPrincipalSolver.js

import {
  roundPrice3,
  roundCents,
  normalizeDenom,
  buildExpandedRows,
  buildSegments,
  computeCapIUse,
  collapseTermGroupsForPresentation,
  shouldBypassDenominationOptimizer,
} from "./solverShared";

/* ────────────────────────────────────────────────
   Level Principal Solver

   Purpose:
   - Deterministic equal-principal amortization
   - Used primarily for commercial loans
   - Supports amortization periods longer than the loan term,
     producing a final balloon principal payment
──────────────────────────────────────────────── */

function computeProceeds({
  scheduleForMath = [],
  costOfIssuance = 0,
  capiUse = 0,
}) {
  const parAmount = roundCents(
    scheduleForMath.reduce((s, r) => s + (Number(r.principal) || 0), 0)
  );

  const grossProceeds = roundCents(
    scheduleForMath.reduce((acc, r) => {
      const p3 = roundPrice3(r.price ?? 100);
      const row = roundCents((Number(r.principal) || 0) * (p3 / 100));
      return acc + row;
    }, 0)
  );

  const coi =
    typeof costOfIssuance === "function"
      ? costOfIssuance(parAmount)
      : Number(costOfIssuance) || 0;

  const netProceeds = roundCents(grossProceeds - coi - capiUse);

  return {
    parAmount,
    grossProceeds,
    netProceeds,
    coi,
    capiUse,
  };
}

function buildLevelPrincipalForPar(params = {}, parAmountInput) {
  const {
    rows = [],
    financingType = "loan",
    couponFrequency = "monthly",
    principalFrequency = "monthly",
    dayCount = "30/360",
    datedDate,
    deliveryDate,
    firstCouponDate,
    firstPrincipalDate,
    finalMaturityDate,
    costOfIssuance = 0,
    dealType,
    capiEnabled = false,
    capiDate = "",
    capiInvestmentRate = 0,
    amortizationPeriods,
  } = params;

  const parAmount = Number(parAmountInput) || 0;

  if (!(parAmount > 0)) {
    throw new Error("parAmount must be > 0");
  }

  const expanded = buildExpandedRows(rows);
  const { segments } = buildSegments({
    expanded,
    firstPrincipalDate,
    finalMaturityDate,
    principalFrequency,
  });

  const termPeriods = segments.length;

  if (!termPeriods) {
    return {
      schedule: [],
      scheduleForMath: [],
      levelPayment: null,
      scheduledPrincipal: 0,
      amortizationPeriods: 0,
      loanTermPeriods: 0,
      balloonAmount: 0,
      parAmount: 0,
      grossProceeds: 0,
      netProceeds: 0,
      coi: 0,
      capiUse: 0,
    };
  }

  const loanAmortizationPeriods = Math.max(
    Number(amortizationPeriods || termPeriods),
    termPeriods
  );

  const scheduleForMath = expanded.map((m) => ({
    ...m,
    principal: 0,
  }));

  const scheduledPrincipalRaw = parAmount / loanAmortizationPeriods;
  const scheduledPrincipal = roundCents(scheduledPrincipalRaw);

  let remainingBalance = roundCents(parAmount);
  let balloonAmount = 0;

  for (let y = 0; y < termPeriods; y++) {
    const principalRowIdx = segments[y].end;
    const isFinalPeriod = y === termPeriods - 1;

    let principalY;

    if (isFinalPeriod) {
      if (loanAmortizationPeriods > termPeriods) {
        balloonAmount = remainingBalance;
      }

      principalY = remainingBalance;
    } else {
      principalY = Math.min(scheduledPrincipal, remainingBalance);
    }

    principalY = roundCents(Math.max(0, principalY));

    scheduleForMath[principalRowIdx].principal = principalY;

    remainingBalance = roundCents(remainingBalance - principalY);

  }

  const capiUse = computeCapIUse({
    capiEnabled,
    dealType,
    capiDate,
    capiInvestmentRate,
    scheduleForMath,
    form: {
      datedDate,
      deliveryDate,
      firstCouponDate,
      finalMaturityDate,
      couponFrequency,
      dayCount,
    },
  });

  const proceeds = computeProceeds({
    scheduleForMath,
    costOfIssuance,
    capiUse,
  });

  const schedule = collapseTermGroupsForPresentation(scheduleForMath);

  return {
    schedule,
    scheduleForMath,

    // Not a level-payment structure.
    levelPayment: null,

    scheduledPrincipal,
    amortizationPeriods: loanAmortizationPeriods,
    loanTermPeriods: termPeriods,
    balloonAmount: roundCents(balloonAmount),

    ...proceeds,

    denominationOptimizationSkipped: shouldBypassDenominationOptimizer({
      financingType,
    }),
  };
}

export function solveLevelPrincipalStructure(params = {}) {
  const {
    targetType = "par_amount",
    targetValue,
    denomination,
    additionalSources = 0,
    financingType = "loan",
  } = params;

  if (!Array.isArray(params.rows) || params.rows.length === 0) {
    return {
      schedule: [],
      scheduleForMath: [],
      levelPayment: null,
      scheduledPrincipal: 0,
      amortizationPeriods: 0,
      loanTermPeriods: 0,
      balloonAmount: 0,
      parAmount: 0,
      grossProceeds: 0,
      netProceeds: 0,
      coi: 0,
      capiUse: 0,
      denominationOptimizationSkipped: shouldBypassDenominationOptimizer({
        financingType,
      }),
    };
  }

  if (targetType === "par_amount") {
    return buildLevelPrincipalForPar(params, Number(targetValue));
  }

  if (targetType === "net_proceeds") {
    const denom = normalizeDenom(denomination);

    const rawTarget = Number(targetValue) || 0;
    const asAmt = Number(additionalSources) || 0;
    const proceedsTarget = Math.max(0, rawTarget - asAmt);

    let par = Math.max(denom, Math.round(proceedsTarget / denom) * denom);
    let result = null;

    for (let i = 0; i < 100; i++) {
      result = buildLevelPrincipalForPar(params, par);

      const netNow = Number(result?.netProceeds || 0);
      const diff = netNow - proceedsTarget;

      if (diff >= 0 && diff < denom) {
        return result;
      }

      const scale = proceedsTarget / (netNow || 1);
      par = par * scale;

      par =
        diff < 0
          ? Math.ceil(par / denom) * denom
          : Math.max(denom, Math.round(par / denom) * denom);

      if (!Number.isFinite(par) || par <= 0) {
        par = denom;
      }
    }

    let guard = 0;

    while (guard < 50) {
      result = buildLevelPrincipalForPar(params, par);

      const netNow = Number(result?.netProceeds || 0);

      if (netNow + 0.005 >= proceedsTarget) {
        console.warn(
          `[Level Principal Solver] Forced sufficiency at par=${par} net=${netNow.toFixed(
            2
          )} target=${proceedsTarget.toFixed(2)}`
        );
        break;
      }

      par += denom;
      guard++;
    }

    return result;
  }

  throw new Error(
    `Unsupported targetType='${targetType}' for level principal structure`
  );
}
